/**
 * Validation Error for Control Plugin Registration
 * Provides detailed error messages for validation failures
 */

/**
 * Detailed validation error with categorized messages
 */
export interface ValidationErrorDetail {
  /** Error field or category */
  field: string;

  /** Error message */
  message: string;

  /** Error severity level */
  severity: 'error' | 'warning';

  /** Optional error code for programmatic handling */
  code?: string;
}

/**
 * Custom error class for control validation failures
 * Provides detailed validation error messages with categorized details
 */
export class ValidationError extends Error {
  /** Detailed validation errors */
  details: ValidationErrorDetail[];

  /** Control type being validated (if available) */
  controlType?: string;

  /**
   * Create a new ValidationError
   * @param message Summary error message
   * @param details Categorized validation error details
   * @param controlType The control type being validated
   */
  constructor(
    message: string,
    details: ValidationErrorDetail[] = [],
    controlType?: string
  ) {
    super(message);
    this.name = 'ValidationError';
    this.details = details;
    this.controlType = controlType;

    // Maintain proper prototype chain for instanceof checks
    Object.setPrototypeOf(this, ValidationError.prototype);
  }

  /**
   * Get all error details as a formatted string
   * @returns Formatted error details
   */
  formatDetails(): string {
    if (this.details.length === 0) {
      return '';
    }

    const errors = this.details.filter((d) => d.severity === 'error');
    const warnings = this.details.filter((d) => d.severity === 'warning');

    let formatted = '';

    if (errors.length > 0) {
      formatted += `\n  Errors:\n`;
      errors.forEach((err) => {
        formatted += `    - ${err.field}: ${err.message}`;
        if (err.code) {
          formatted += ` [${err.code}]`;
        }
        formatted += '\n';
      });
    }

    if (warnings.length > 0) {
      formatted += `\n  Warnings:\n`;
      warnings.forEach((warn) => {
        formatted += `    - ${warn.field}: ${warn.message}`;
        if (warn.code) {
          formatted += ` [${warn.code}]`;
        }
        formatted += '\n';
      });
    }

    return formatted;
  }

  /**
   * Get full error message with details
   * @returns Complete error message
   */
  override toString(): string {
    let msg = `${this.name}: ${this.message}`;
    if (this.controlType) {
      msg += ` (Control: ${this.controlType})`;
    }
    const details = this.formatDetails();
    if (details) {
      msg += details;
    }
    return msg;
  }

  /**
   * Check if validation has critical errors
   * @returns True if there are error-level details
   */
  hasCriticalErrors(): boolean {
    return this.details.some((d) => d.severity === 'error');
  }

  /**
   * Check if validation has warnings only
   * @returns True if there are only warnings (no errors)
   */
  hasWarningsOnly(): boolean {
    return (
      this.details.some((d) => d.severity === 'warning') &&
      !this.hasCriticalErrors()
    );
  }

  /**
   * Get details by severity
   * @param severity The severity level to filter
   * @returns Details matching the severity
   */
  getDetailsBySeverity(
    severity: 'error' | 'warning'
  ): ValidationErrorDetail[] {
    return this.details.filter((d) => d.severity === severity);
  }

  /**
   * Get details by field
   * @param field The field name to search
   * @returns Details for the specified field
   */
  getDetailsByField(field: string): ValidationErrorDetail[] {
    return this.details.filter((d) => d.field === field);
  }
}
